import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input } from 'components/atoms';
import { DescriptiveInputWrapper, FormInput } from 'components/molecules';
import PopupForm from 'components/molecules/popup/form/PopupForm';
import { passwordRegExp } from 'constants/passwordRegExp';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useSoundEffect } from 'hooks/notification/useSoundEffect';
import i18next, { t } from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { useRef } from 'react';
import {
  PiFingerprintSimple,
  PiQrCode,
  PiShieldCheck,
  PiShieldSlash,
  PiSpeakerHigh,
  PiSpeakerX,
} from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useChangePassword } from 'services/auth/changePassword';
import { useDisableTwoFactor } from 'services/auth/disableTwoFactor';
import {
  useCodeOtpQR,
  useConfirmTwoFactorOTP,
} from 'services/auth/oneTimePassword';
import { useGetTwoFactorStatus, useTwoFactor } from 'services/auth/twoFactor';
import { IChangePasswordForm } from 'services/auth/types';
import { THEME_TYPE, useTokenData } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { object, ref, string } from 'yup';

const newPasswordInitialValues: IChangePasswordForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const newPasswordValidationSchema = object().shape({
  currentPassword: string().required(i18next.t('formik-error.required')),
  newPassword: string()
    .matches(passwordRegExp, i18next.t('formik-error.invalid-password'))
    .required(i18next.t('formik-error.required')),
  confirmPassword: string()
    .oneOf([ref('newPassword'), ''], i18next.t('formik-error.unmatch-password'))
    .required(i18next.t('formik-error.required')),
});

interface IOneTimePasswordForm {
  code: string;
}

const oneTimePasswordInitialValues: IOneTimePasswordForm = {
  code: '',
};

const UserSettings = () => {
  const { t } = useTranslation();
  const changePassword = useChangePassword();

  const {
    setThemeType,
    themeType,
    theme,
    soundVolume,
    setSoundVolume,
    isSoundMuted,
    setIsSoundMuted,
  } = useStoreState();
  const tokenData = useTokenData();

  const mutateOprions = useMutateOptions<IChangePasswordForm>();
  const { getSelectOptions } = useEnumToSelectOptions();
  const queryClient = useQueryClient();

  const { mutate: getOtpQR, data: qrCodeData } = useCodeOtpQR();
  const { mutateAsync: confirmOtp, isPending: isConfirmPending } =
    useConfirmTwoFactorOTP();
  const { mutate: enableTwoFactor, isPending: isTwoFactorPending } =
    useTwoFactor();
  const { data: twoFactorStatus } = useGetTwoFactorStatus(
    tokenData?.UserId,
    true,
  );
  const disableTwoFactor = useDisableTwoFactor(tokenData?.UserId || '');
  const otpPopupOpenedRef = useRef(false);

  const isTwoFactorEnabled = twoFactorStatus?.isTwoFactorEnabled ?? false;
  const isOtpEnabled = twoFactorStatus?.isOtpEnabled ?? false;
  const handleTwoFactorToggle = () => {
    if (isTwoFactorEnabled && tokenData?.UserId) {
      disableTwoFactor.mutate(tokenData.UserId, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['two-factor-status', tokenData.UserId],
          });
        },
      });
    } else {
      enableTwoFactor(undefined, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['two-factor-status', tokenData?.UserId],
          });
        },
      });
    }
  };

  const onChangePassword = async (
    values: IChangePasswordForm,
    formikHelpers: FormikHelpers<IChangePasswordForm>,
    actions: PopupActions | null,
  ) =>
    await changePassword.mutateAsync(values, {
      ...mutateOprions(formikHelpers),
      onSuccess: () => {
        actions?.close();
        formikHelpers.resetForm();
      },
    });

  const handleOtpPopupOpen = () => {
    if (tokenData?.UserId && !otpPopupOpenedRef.current) {
      otpPopupOpenedRef.current = true;
      getOtpQR({ userId: tokenData.UserId });
    }
  };

  const handleOtpPopupClose = () => {
    otpPopupOpenedRef.current = false;
  };

  const onOneTimePassword = async (
    values: IOneTimePasswordForm,
    formikHelpers: FormikHelpers<IOneTimePasswordForm>,
    actions: PopupActions | null,
  ) => {
    if (tokenData?.UserId && values.code) {
      await confirmOtp(
        { userId: tokenData.UserId, code: values.code },
        {
          onSuccess: () => {
            actions?.close();
            formikHelpers.resetForm();
            handleOtpPopupClose();
          },
        },
      );
    }
  };
  const play = useSoundEffect();
  return (
    <div className='flex flex-col gap-[30px] w-[370px]'>
      {!!theme?.canChangeThemeMode && (
        <DescriptiveInputWrapper
          header={t('setting.user.theme')}
          description={t('setting.user.themeDescription')}>
          <Input.Select
            label={t('input.label.defaultThemeMode')}
            options={getSelectOptions(THEME_TYPE, 'enum.THEME_TYPE')}
            isClearable={false}
            value={{
              label: t(`enum.THEME_TYPE.${THEME_TYPE[themeType ?? 1]}`),
              value: themeType,
            }}
            onChange={o => {
              localStorage.setItem('theme-mode', `${o}`);
              setThemeType(o.value as THEME_TYPE);
            }}
          />
        </DescriptiveInputWrapper>
      )}
      <DescriptiveInputWrapper
        header={t('setting.user.sound')}
        description={t('setting.user.soundDescription')}>
        <div className='flex gap-4 w-full p-8 items-center'>
          <Button
            isClear
            onlyIcon
            onClick={() => {
              setIsSoundMuted(!isSoundMuted);
              if (isSoundMuted) {
                play(soundVolume);
              }
            }}>
            <Button.Description description={t('button.toggle-sound')} />

            <Button.Icon
              icon={!isSoundMuted ? <PiSpeakerHigh /> : <PiSpeakerX />}
            />
          </Button>
          <Input.Slider
            min={0}
            max={100}
            value={isSoundMuted ? 0 : soundVolume * 100}
            onChange={e => {
              setSoundVolume(parseInt(e.target.value) * 0.01);
              setIsSoundMuted(false);
            }}
            onMouseUp={e => play(soundVolume)}
          />
        </div>
      </DescriptiveInputWrapper>
      <DescriptiveInputWrapper
        header={t('setting.user.security')}
        description={t('setting.user.securityDescription')}>
        <PopupForm
          initialValues={newPasswordInitialValues}
          onSubmit={onChangePassword}
          validationSchema={newPasswordValidationSchema}
          submitButton={
            <Button type='submit' variant={BUTTON_VARIANT.submit}>
              <Button.Text text={t('button.change')} />
            </Button>
          }
          header={
            <>
              <PiFingerprintSimple /> {t('reset-password.button')}
            </>
          }
          trigger={
            <Button>
              <Button.Icon icon={<PiFingerprintSimple />} />
              <Button.Text text={t('reset-password.button')} />
            </Button>
          }>
          <FormInput.Text
            name='currentPassword'
            type='password'
            label={t('input.label.current-password')}
          />
          <div />
          <FormInput.Text
            name='newPassword'
            type='password'
            label={t('input.label.new-password')}
          />
          <FormInput.Text
            name='confirmPassword'
            type='password'
            label={t('input.label.confirm-password')}
          />
        </PopupForm>
      </DescriptiveInputWrapper>
      <DescriptiveInputWrapper
        header={t('setting.user.twoFactor')}
        description={t('setting.user.twoFactorDescription')}>
        <Button
          onClick={handleTwoFactorToggle}
          variant={
            isTwoFactorEnabled ? BUTTON_VARIANT.remove : BUTTON_VARIANT.submit
          }
          isLoading={isTwoFactorPending || disableTwoFactor.isPending}>
          <Button.Icon
            icon={isTwoFactorEnabled ? <PiShieldSlash /> : <PiShieldCheck />}
          />
          <Button.Text
            text={t(
              isTwoFactorEnabled
                ? 'button.disableTwoFactor'
                : 'button.enableTwoFactor',
            )}
          />
        </Button>
      </DescriptiveInputWrapper>
      {isTwoFactorEnabled && (
        <DescriptiveInputWrapper
          header={t('setting.user.oneTimePassword')}
          description={t('setting.user.oneTimePasswordDescription')}>
          <PopupForm
            initialValues={oneTimePasswordInitialValues}
            onSubmit={onOneTimePassword}
            submitButton={
              <Button
                type='submit'
                variant={BUTTON_VARIANT.submit}
                isLoading={isConfirmPending}>
                <Button.Text text={t('oneTimePassword.enable')} />
              </Button>
            }
            header={
              <>
                <PiQrCode /> {t('oneTimePassword.title')}
              </>
            }
            onOpen={handleOtpPopupOpen}
            onClose={handleOtpPopupClose}
            trigger={
              <Button
                variant={
                  isOtpEnabled ? BUTTON_VARIANT.default : BUTTON_VARIANT.submit
                }
                disabled={isOtpEnabled}>
                <Button.Icon
                  icon={isOtpEnabled ? <PiShieldCheck /> : <PiQrCode />}
                />
                <Button.Text
                  text={t(
                    isOtpEnabled
                      ? 'button.oneTimePasswordEnabled'
                      : 'button.enableOneTimePassword',
                  )}
                />
              </Button>
            }>
            {qrCodeData && (
              <div className='flex justify-center mb-4'>
                <img
                  src={`data:image/png;base64,${qrCodeData}`}
                  alt='Kod QR'
                  width={200}
                  height={200}
                  style={{ maxWidth: 200, maxHeight: 200 }}
                />
              </div>
            )}
            <div className='flex justify-center mb-4'>
              <FormInput.Text
                name='code'
                label={t('oneTimePassword.enterCode')}
                className='w-64 text-center'
              />
            </div>
          </PopupForm>
        </DescriptiveInputWrapper>
      )}
    </div>
  );
};

export default UserSettings;
